import {
  DEFAULT_SECONDARY_HARMONY,
  generateStorefrontPalette,
  hexToOklch,
  isHexColor,
  paletteContrastReport,
  SECONDARY_HARMONIES,
  type SecondaryHarmony,
  STOREFRONT_TOKEN_DEFAULTS,
  STOREFRONT_TOKEN_GROUPS,
  type StorefrontPalette,
  type StorefrontTokenKey,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@loot-os/ui/components/tooltip";
import { cn } from "@loot-os/ui/lib/utils";
import { ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

import { ColorField } from "./StorefrontFields";
import { StorefrontThemeScope } from "./StorefrontThemeScope";

const HEX6 = /^#[0-9a-fA-F]{6}$/;

/** The token's saved value if the color picker can show it, else the stock one. */
function pickerSafe(saved: StorefrontPalette, key: StorefrontTokenKey): string {
  return HEX6.test(saved[key]) ? saved[key] : STOREFRONT_TOKEN_DEFAULTS[key];
}

const CHART_KEYS = ["chart-1", "chart-2", "chart-3", "chart-4", "chart-5"] as const;

/**
 * Three brand colors in, a whole palette out. The seeds live here only: each edit of a seed
 * writes the generated tokens into the Theme tab's draft through `onGenerate`, and the tab's one
 * save stores them (its review lists every variable that changed). Check the palette opens the
 * generated swatches, a sample card and the contrast report, all of the draft's own palette
 * (`current`), so opening the tab never shows a palette that is not the store's; while closed it
 * still says whether any pair falls short of AA.
 *
 * Blank primary and background fields mean "the current value" (their placeholder). A blank
 * secondary means "derive one", and the harmony picker beside it decides how, triadic by default.
 */
export function StorefrontPaletteGenerator({
  current,
  radius,
  fontFamily,
  headingFontFamily,
  canEdit,
  onGenerate,
}: {
  /** The draft's palette: what the tab would save now. */
  current: StorefrontPalette;
  radius?: string;
  fontFamily?: string;
  headingFontFamily?: string;
  canEdit: boolean;
  onGenerate: (tokens: StorefrontPalette) => void;
}) {
  const [primary, setPrimary] = useState("");
  const [background, setBackground] = useState("");
  // Seeded with the current secondary only when it is a real color of its own;
  // one equal to the muted surface is the derived kind and stays blank.
  const [secondary, setSecondary] = useState(() =>
    current.secondary !== current.muted && HEX6.test(current.secondary) ? current.secondary : "",
  );
  const [harmony, setHarmony] = useState<SecondaryHarmony>(DEFAULT_SECONDARY_HARMONY);
  const [checking, setChecking] = useState(false);

  /** Generates from the seeds with one of them replaced, and hands a valid palette to the draft. */
  const generate = (next: {
    primary?: string;
    background?: string;
    secondary?: string;
    harmony?: SecondaryHarmony;
  }) => {
    const primarySeed = (next.primary ?? primary).trim() || pickerSafe(current, "primary");
    const backgroundSeed =
      (next.background ?? background).trim() || pickerSafe(current, "background");
    const secondarySeed = (next.secondary ?? secondary).trim();
    if (!isHexColor(primarySeed) || !isHexColor(backgroundSeed)) return;
    if (secondarySeed && !isHexColor(secondarySeed)) return;
    onGenerate(
      generateStorefrontPalette({
        primary: primarySeed,
        secondary: secondarySeed || undefined,
        background: backgroundSeed,
        harmony: next.harmony ?? harmony,
      }),
    );
  };

  const secondarySeed = secondary.trim();
  const seedsInvalid =
    (primary.trim() !== "" && !isHexColor(primary.trim())) ||
    (background.trim() !== "" && !isHexColor(background.trim())) ||
    (secondarySeed !== "" && !isHexColor(secondarySeed));
  const preview = current;

  const report = useMemo(() => paletteContrastReport(preview), [preview]);
  const failing = report.filter((row) => !row.pass).length;
  const backgroundL = hexToOklch(preview.background)?.L;
  const midLightness = backgroundL !== undefined && backgroundL > 0.4 && backgroundL < 0.7;

  return (
    <div className="grid gap-4">
      <div className="grid gap-4 @lg:grid-cols-3">
        <ColorField
          id="palette-seed-primary"
          label="Primary"
          hint="buttons, links"
          fallback={pickerSafe(current, "primary")}
          value={primary}
          onChange={(value) => {
            setPrimary(value);
            generate({ primary: value });
          }}
        />
        <ColorField
          id="palette-seed-background"
          label="Background"
          hint="page"
          fallback={pickerSafe(current, "background")}
          value={background}
          onChange={(value) => {
            setBackground(value);
            generate({ background: value });
          }}
        />
        <ColorField
          id="palette-seed-secondary"
          label="Secondary"
          hint="optional"
          fallback={pickerSafe(current, "secondary")}
          value={secondary}
          onChange={(value) => {
            setSecondary(value);
            generate({ secondary: value });
          }}
        />
      </div>
      {secondarySeed === "" && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Label htmlFor="palette-harmony" className="text-xs font-normal text-muted-foreground">
            No secondary? One is picked from the primary:
          </Label>
          <Select
            value={harmony}
            onValueChange={(value) => {
              setHarmony(value as SecondaryHarmony);
              generate({ harmony: value as SecondaryHarmony });
            }}
            disabled={!canEdit}
          >
            <SelectTrigger id="palette-harmony" size="sm" className="w-auto">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SECONDARY_HARMONIES.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {seedsInvalid ? (
        <p className="text-sm text-destructive">
          Enter hex colors (#rrggbb) for the primary, background and secondary to generate a
          palette.
        </p>
      ) : (
        <div className="grid gap-3">
          <button
            type="button"
            onClick={() => setChecking((open) => !open)}
            aria-expanded={checking}
            className="-mx-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent"
          >
            <ChevronRight
              className={cn(
                "size-4 shrink-0 text-muted-foreground transition-transform",
                checking && "rotate-90",
              )}
              aria-hidden
            />
            <span className="font-medium">Check the palette</span>
            <span
              className={cn(
                "ml-auto text-xs",
                failing > 0 ? "font-medium text-destructive" : "text-muted-foreground",
              )}
            >
              {failing === 0
                ? "Every pair reads clearly"
                : `${failing} ${failing === 1 ? "pair falls" : "pairs fall"} short of AA contrast`}
            </span>
          </button>
          {checking && (
            <>
              {/* One row per token group: every color the seeds work out. */}
              <TooltipProvider>
                <div className="grid gap-2">
                  {STOREFRONT_TOKEN_GROUPS.map((group) => (
                    <div key={group.label} className="grid grid-cols-[4rem_minmax(0,1fr)] gap-3">
                      {/* The label's tooltip lists the group's variables, one per swatch, left to right. */}
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <span className="cursor-default self-center text-xs text-muted-foreground" />
                          }
                        >
                          {group.label}
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          <span className="grid gap-0.5 font-mono">
                            {group.tokens.map((token) => (
                              <span key={token.key}>--{token.key}</span>
                            ))}
                          </span>
                        </TooltipContent>
                      </Tooltip>
                      <span
                        className="grid min-h-7 gap-1.5"
                        style={{
                          gridTemplateColumns: `repeat(${group.tokens.length}, minmax(0, 1fr))`,
                        }}
                      >
                        {group.tokens.map((token) => (
                          <Tooltip key={token.key}>
                            <TooltipTrigger
                              render={
                                <span
                                  role="img"
                                  aria-label={`--${token.key} ${preview[token.key]}`}
                                  className="rounded-md ring-1 ring-border"
                                  style={{ backgroundColor: preview[token.key] }}
                                />
                              }
                            />
                            <TooltipContent>
                              <span className="font-mono">--{token.key}</span>
                              <span className="font-mono opacity-70">{preview[token.key]}</span>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              </TooltipProvider>

              <div className="grid gap-4 @4xl:grid-cols-[minmax(0,1fr)_minmax(18rem,24rem)]">
                <StorefrontThemeScope
                  theme={{ tokens: preview, radius, fontFamily, headingFontFamily }}
                  className="rounded-lg p-4 ring-1 ring-border"
                >
                  <Card>
                    <CardHeader>
                      {/* A real heading, so it previews the heading font as the storefront's do. */}
                      <CardTitle>
                        <h3>Mystery box</h3>
                      </CardTitle>
                      <CardDescription>
                        How cards, buttons and text read in this palette.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-3">
                      <div className="flex flex-wrap gap-2">
                        <Button type="button">Open box</Button>
                        <Button type="button" variant="secondary">
                          Details
                        </Button>
                        <Button type="button" variant="ghost">
                          Odds
                        </Button>
                        <Button type="button" variant="destructive">
                          Remove
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge>Legendary</Badge>
                        <Badge variant="secondary">Rare</Badge>
                        <Badge variant="outline">Common</Badge>
                      </div>
                      <Input readOnly placeholder="Search boxes…" aria-label="Preview input" />
                      <p className="text-sm text-muted-foreground">
                        1,204 opened this week · muted text on the card surface.
                      </p>
                      <div className="flex gap-1" aria-hidden>
                        {CHART_KEYS.map((key) => (
                          <span
                            key={key}
                            className="h-2 flex-1 rounded-full"
                            style={{ backgroundColor: preview[key] }}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </StorefrontThemeScope>

                <div className="grid min-w-0 content-start gap-1.5 text-sm">
                  <span className="text-xs text-muted-foreground">Contrast (WCAG 2)</span>
                  {report.map((row) => (
                    <div
                      key={`${row.fg}/${row.bg}`}
                      className="flex min-w-0 items-center justify-between gap-3"
                    >
                      <code className="min-w-0 truncate font-mono text-xs text-muted-foreground">
                        {row.fg} / {row.bg}
                      </code>
                      <span className="flex shrink-0 items-baseline gap-2 text-xs tabular-nums">
                        {Number.isNaN(row.ratio) ? "—" : row.ratio.toFixed(2)}
                        <span
                          className={`w-14 text-right ${
                            row.pass ? "text-muted-foreground" : "font-medium text-destructive"
                          }`}
                        >
                          {row.pass ? (row.min === 4.5 ? "AA" : "AA large") : `< ${row.min}`}
                        </span>
                      </span>
                    </div>
                  ))}
                  {midLightness && (
                    <p className="pt-1 text-xs text-muted-foreground">
                      A mid-lightness background can't reach AA text contrast with any color — go
                      darker or lighter for readable text.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
